/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import axios from "axios";
import { Action, Dispatch } from "redux";
import { sAccessNoteFail } from "assets/jss/views/ResStrings";

import { lecNotesLoaded, lecsWithNotesLoaded } from "../selectors/noteSelector";

import {
  GET_LEC_NOTES,
  FIND_NOTES,
  DELETE_NOTE,
  UPDATE_NOTE,
  EXPAND_NOTES,
  EXPAND_NOTE,
  GET_LECS_WITH_NOTES,
  GET_NOTES_FAILED,
  SHOW_TOAST,
  ApricotActionCreator,
} from "./types";

type NoteLectureFilter = {
  user: string;
  course: string;
  lecture?: string;
};

type NoteFindFilter = {
  user: string;
  keyword: string;
};

const inTest = process.env.NODE_ENV === "test";

// the 'api' passed in is the axiosInstance we created

export const updateNote: ApricotActionCreator = (data) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  return dispatch({
    type: UPDATE_NOTE,
    payload: data,
  });
};

export const deleteNote: ApricotActionCreator = (data) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  return dispatch({
    type: DELETE_NOTE,
    payload: data,
  });
};

export const getNotes: ApricotActionCreator = (
  filter: NoteLectureFilter,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    const { user, course, lecture } = filter; // filter: { user, course, lecture }

    if (!inTest && lecNotesLoaded(getState().state_notes, { lecture })) {
      return dispatch({
        type: GET_LEC_NOTES, // for changing the curLecture in store
        payload: { lecture, notes: null }, // notes: null means we already have the data loaded
      });
    } else {
      api = api || axios;
      const { data } = await api.get(`/api/nt/${user}/${course}/${lecture}`);

      return dispatch({
        type: GET_LEC_NOTES,
        payload: { lecture, notes: data }, // data: [{ _id, text, clr, vpos }, ... ]
      });
    }
  } catch (err) {
    dispatch({
      type: SHOW_TOAST,
      payload: { type: "warn", message: sAccessNoteFail },
    });
    return dispatch({
      type: GET_NOTES_FAILED,
      payload: null,
    });
  }
};

export const expandNotes: ApricotActionCreator = (params) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  const { lecture, expanded, noteId } = params;
  if (noteId) {
    return dispatch({
      type: EXPAND_NOTE,
      payload: { lecture, expanded, noteId },
    });
  } else {
    return dispatch({
      type: EXPAND_NOTES,
      payload: { lecture, expanded },
    });
  }
};

export const findNotes: ApricotActionCreator = (
  filter: NoteFindFilter,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    api = api || axios;
    const { user, ...rest } = filter;
    const { data } = await api.get(`/api/ntf/${user}`, rest);

    return dispatch({
      type: FIND_NOTES,
      payload: data,
    });
  } catch (err) {
    dispatch({
      type: SHOW_TOAST,
      payload: { type: "warn", message: sAccessNoteFail },
    });
    return dispatch({
      type: GET_NOTES_FAILED,
      payload: null,
    });
  }
};

export const getLecsWithNotes: ApricotActionCreator = (
  filter: NoteLectureFilter,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    if (
      !inTest &&
      lecsWithNotesLoaded(getState().state_notes, { course: filter.course })
    ) {
      return Promise.resolve() as any;
    }

    api = api || axios;
    const { user, course } = filter; // filter: { user, course }
    const { data } = await api.get(`/api/ntl/${user}/${course}`);

    return dispatch({
      type: GET_LECS_WITH_NOTES,
      payload: { course, lecList: data }, // data: [ lid1, lid2, ...]
    });
  } catch (err) {
    dispatch({
      type: SHOW_TOAST,
      payload: { type: "warn", message: sAccessNoteFail },
    });
    return dispatch({
      type: GET_NOTES_FAILED,
      payload: null,
    });
  }
};

export type ApricotAction = ApricotActionCreator;
